import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Video, Play, X, BookOpen, Search, MessageSquare, Send, Maximize2, Minimize2, Users, Eye, Loader2, Home, AlertTriangle } from 'lucide-react';
import axios from 'axios';

// Detectar Samsung Browser
const isSamsungBrowser = () => {
  return /SamsungBrowser/i.test(navigator.userAgent);
};

/**
 * ZoomSDKTest - Página de prueba para Zoom embebido
 * 
 * Puede recibir parámetros por URL:
 * - meeting: ID de la reunión
 * - pwd: Contraseña
 * - name: Nombre del usuario
 * - autostart: Si es "true", inicia directamente sin formulario
 */
const ZoomSDKTest = () => {
  const { API, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showSamsungWarning, setShowSamsungWarning] = useState(isSamsungBrowser());
  
  // Obtener parámetros de URL (vendrán del widget)
  const urlMeeting = searchParams.get('meeting');
  const urlPassword = searchParams.get('pwd');
  const urlUserName = searchParams.get('name');
  const autoStart = searchParams.get('autostart') === 'true';
  
  const [meetingNumber, setMeetingNumber] = useState(urlMeeting || '86943396227');
  const [meetingPassword, setMeetingPassword] = useState(urlPassword || '602068');
  const [userName, setUserName] = useState(urlUserName || user?.nombre || 'Estudiante');
  const [className, setClassName] = useState('Cargando...');
  const [loadingMeetingInfo, setLoadingMeetingInfo] = useState(true);
  const [showMeeting, setShowMeeting] = useState(false); // Cambiado: no autostart hasta permisos
  const [zoomWindowOpened, setZoomWindowOpened] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Estado de permisos de cámara/audio
  const [showPermissionModal, setShowPermissionModal] = useState(autoStart); // Mostrar si viene de widget
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [requestingPermissions, setRequestingPermissions] = useState(false);
  
  // Panel activo: null, 'bible', 'notes'
  const [activePanel, setActivePanel] = useState(null);
  
  // Modo preview para admin
  const [previewAsStudent, setPreviewAsStudent] = useState(false);
  
  // Estado de la Biblia
  const [bibleSearch, setBibleSearch] = useState('');
  const [bibleResults, setBibleResults] = useState([]);
  const [bibleLoading, setBibleLoading] = useState(false);
  
  // Estado de Notas Públicas
  const [publicNotes, setPublicNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);

  // Versículos mostrados en pantalla
  const [displayedVerses, setDisplayedVerses] = useState([]);

  const isStudent = ['estudiante', 'pastor', 'graduado'].includes(user?.rol) || previewAsStudent;
  const isRealAdmin = user?.rol === 'admin' || user?.rol === 'profesor';

  // Obtener nombre de la reunión desde Zoom
  useEffect(() => {
    const fetchMeetingInfo = async () => {
      setLoadingMeetingInfo(true);
      try {
        const response = await axios.get(`${API}/zoom-sdk-test/meeting-info/${meetingNumber}`);
        if (response.data.topic) {
          setClassName(response.data.topic);
        } else {
          setClassName('Clase en Vivo');
        }
      } catch (error) {
        console.error('Error obteniendo info de reunión:', error);
        setClassName('Clase en Vivo');
      } finally {
        setLoadingMeetingInfo(false);
      }
    };
    
    if (meetingNumber) {
      fetchMeetingInfo();
    }
  }, [API, meetingNumber]);

  // Solicitar permisos de cámara y micrófono
  const requestMediaPermissions = async () => {
    setRequestingPermissions(true);
    setPermissionError(null);
    
    try {
      // Solicitar permisos de cámara y micrófono
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      // Permisos concedidos - detener el stream (Zoom lo usará después)
      stream.getTracks().forEach(track => track.stop());
      
      setPermissionsGranted(true);
      setShowPermissionModal(false);
      setShowMeeting(true);
      
    } catch (error) {
      console.error('Error solicitando permisos:', error);
      
      if (error.name === 'NotAllowedError') {
        setPermissionError('Permisos denegados. Por favor permite el acceso a la cámara y micrófono en la configuración de tu navegador.');
      } else if (error.name === 'NotFoundError') {
        setPermissionError('No se encontró cámara o micrófono en tu dispositivo.');
      } else {
        setPermissionError('Error al solicitar permisos. Intenta de nuevo o usa otro navegador.');
      }
    } finally {
      setRequestingPermissions(false);
    }
  };

  // Entrar sin cámara (solo audio o como espectador)
  const enterWithoutCamera = () => {
    setShowPermissionModal(false);
    setShowMeeting(true);
  };

  // URL de Zoom - Formato optimizado para evitar que pida contraseña
  const getZoomWebUrl = () => {
    const encodedName = encodeURIComponent(userName);
    // Usar formato con password codificado correctamente
    return `https://zoom.us/wc/join/${meetingNumber}?pwd=${encodeURIComponent(meetingPassword)}&uname=${encodedName}&embed=true`;
  };

  // ============ BIBLIA ============
  const searchBible = async () => {
    if (!bibleSearch.trim() || bibleSearch.length < 3) return;
    
    setBibleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const referencePattern = /^([1-3]?\s?[A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/i;
      const match = bibleSearch.trim().match(referencePattern);
      
      if (match) {
        const [, bookName, chapter, verseStart, verseEnd] = match;
        const booksResponse = await axios.get(`${API}/bible/books`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const book = booksResponse.data.books.find(b => 
          b.name.toLowerCase().includes(bookName.toLowerCase().trim())
        );
        
        if (book) {
          const chapterResponse = await axios.get(`${API}/bible/${book.id}/${chapter}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          let verses = chapterResponse.data.data.verses || [];
          
          if (verseStart) {
            const start = parseInt(verseStart);
            const end = verseEnd ? parseInt(verseEnd) : start;
            verses = verses.filter(v => v.verse >= start && v.verse <= end);
          }
          
          setBibleResults(verses.map(v => ({
            reference: `${book.name} ${chapter}:${v.verse}`,
            text: v.text
          })));
        }
      } else {
        const response = await axios.get(`${API}/bible/search?q=${encodeURIComponent(bibleSearch)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBibleResults(response.data.results || []);
      }
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setBibleLoading(false);
    }
  };

  const addVerseToDisplay = (verse) => {
    if (!displayedVerses.find(v => v.reference === verse.reference)) {
      setDisplayedVerses(prev => [...prev, verse]);
    }
  };

  const removeVerseFromDisplay = (reference) => {
    setDisplayedVerses(prev => prev.filter(v => v.reference !== reference));
  };

  // ============ NOTAS PÚBLICAS ============
  const fetchNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zoom-sdk-test/notes/${meetingNumber}`);
      setPublicNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error obteniendo notas:', error);
    }
  }, [API, meetingNumber]);

  useEffect(() => {
    if (showMeeting) {
      fetchNotes();
      const interval = setInterval(fetchNotes, 3000);
      return () => clearInterval(interval);
    }
  }, [showMeeting, fetchNotes]);

  const sendNote = async () => {
    if (!newNote.trim()) return;
    
    setNotesLoading(true);
    try {
      await axios.post(`${API}/zoom-sdk-test/notes`, {
        meeting_number: meetingNumber,
        author_name: userName,
        content: newNote.trim(),
        note_type: 'note'
      });
      setNewNote('');
      fetchNotes();
    } catch (error) {
      console.error('Error enviando nota:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  const shareVerse = async (verse) => {
    try {
      await axios.post(`${API}/zoom-sdk-test/notes`, {
        meeting_number: meetingNumber,
        author_name: userName,
        content: `📖 ${verse.reference}: "${verse.text}"`,
        note_type: 'verse'
      });
      fetchNotes();
    } catch (error) {
      console.error('Error compartiendo versículo:', error);
    }
  };

  // Abrir Zoom en ventana nueva al entrar
  useEffect(() => {
    if (showMeeting && !zoomWindowOpened) {
      const zoomUrl = getZoomWebUrl();
      window.open(zoomUrl, '_blank', 'noopener');
      setZoomWindowOpened(true);
    }
  }, [showMeeting]); // eslint-disable-line

  // ============ MODAL DE PERMISOS ============
  if (showPermissionModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Video className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-bold">Preparar Cámara y Audio</h2>
            <p className="text-sm opacity-90 mt-1">
              {className !== 'Cargando...' ? className : 'Clase en Vivo'}
            </p>
          </div>

          {/* Contenido */}
          <div className="p-6 space-y-4">
            <p className="text-gray-600 text-center">
              Para participar en la clase, necesitas habilitar tu cámara y micrófono.
            </p>

            {/* Indicadores de permisos */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">📹</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Cámara</p>
                  <p className="text-sm text-gray-500">Para que te vean en clase</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">🎤</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Micrófono</p>
                  <p className="text-sm text-gray-500">Para que te escuchen</p>
                </div>
              </div>
            </div>

            {/* Error */}
            {permissionError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <p>{permissionError}</p>
                </div>
              </div>
            )}

            {/* Aviso Samsung */}
            {isSamsungBrowser() && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Samsung Browser detectado.</strong> Para mejor compatibilidad con la cámara, 
                    te recomendamos usar <strong>Chrome</strong> o <strong>Brave</strong>.
                  </p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="space-y-2 pt-2">
              <Button
                onClick={requestMediaPermissions}
                disabled={requestingPermissions}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 rounded-xl text-lg"
              >
                {requestingPermissions ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Solicitando permisos...
                  </>
                ) : (
                  <>
                    📹 Permitir Cámara y Audio
                  </>
                )}
              </Button>
              
              <Button
                onClick={enterWithoutCamera}
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700"
              >
                Entrar sin cámara (solo ver)
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
            <button
              onClick={() => navigate('/app/dashboard')}
              className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
            >
              <Home className="h-4 w-4" />
              Volver al inicio
            </button>
            <span className="text-xs text-gray-400">
              Política del Instituto: cámaras encendidas
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ============ VISTA EN CLASE ============
  if (showMeeting) {
    return (
      <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[calc(100vh-80px)]'} bg-gradient-to-b from-slate-900 to-slate-800`}>

        {/* Header elegante con nombre de la clase desde Zoom */}
        <div className="text-center py-3 px-4">
          <h1 className="text-white text-xl font-light tracking-wide">
            Bienvenidos a{' '}
            <span className="font-semibold text-blue-300">
              {loadingMeetingInfo ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando...
                </span>
              ) : className}
            </span>
          </h1>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 flex overflow-hidden relative">
          
          {/* Área principal - Info de Zoom + herramientas */}
          <div className="flex-1 relative mx-2 mb-2 rounded-xl overflow-hidden shadow-2xl bg-slate-800 flex flex-col items-center justify-center">
            {/* Versículos mostrados */}
            {displayedVerses.length > 0 && (
              <div className="absolute top-4 left-4 right-4 z-10">
                <div className="bg-gradient-to-r from-amber-900/90 to-amber-800/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-amber-600/30">
                  {displayedVerses.map((verse, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-white mb-2 last:mb-0">
                      <button 
                        onClick={() => removeVerseFromDisplay(verse.reference)}
                        className="text-amber-300 hover:text-white mt-1 flex-shrink-0 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div>
                        <span className="font-bold text-amber-200 text-lg">{verse.reference}</span>
                        <p className="text-amber-50 mt-1 leading-relaxed">{verse.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Indicador de Zoom abierto */}
            <div className="text-center p-8">
              <div className="w-20 h-20 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-10 h-10 text-blue-400" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">La clase se abrió en otra pestaña</h2>
              <p className="text-slate-400 text-sm mb-4 max-w-md">
                Zoom está funcionando en una ventana separada. Usa esta página para buscar versículos y tomar notas durante la clase.
              </p>
              <Button
                onClick={() => window.open(getZoomWebUrl(), '_blank', 'noopener')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Video className="w-4 h-4 mr-2" />
                Volver a abrir Zoom
              </Button>
            </div>
          </div>

          {/* Panel lateral con animación */}
          {activePanel && (
            <div className="w-80 bg-white rounded-xl mr-2 mb-2 flex flex-col shadow-2xl overflow-hidden animate-in slide-in-from-right duration-300">
              
              {/* Panel de Biblia */}
              {activePanel === 'bible' && (
                <>
                  <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold flex items-center gap-2">
                        <BookOpen className="h-5 w-5" />
                        Biblia
                      </h3>
                      <button onClick={() => setActivePanel(null)} className="hover:bg-white/20 rounded-full p-1 transition-colors">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-3 border-b bg-gray-50">
                    <div className="flex gap-2">
                      <Input
                        value={bibleSearch}
                        onChange={(e) => setBibleSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchBible()}
                        placeholder="Juan 3:16"
                        className="text-sm"
                      />
                      <Button onClick={searchBible} size="sm" disabled={bibleLoading} className="bg-indigo-600 hover:bg-indigo-700">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3">
                    {bibleLoading ? (
                      <p className="text-center text-gray-500 py-4">Buscando...</p>
                    ) : bibleResults.length > 0 ? (
                      <div className="space-y-3">
                        {bibleResults.map((result, idx) => (
                          <div key={idx} className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl text-sm border border-indigo-100">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className="font-bold text-indigo-700">{result.reference}</span>
                              <div className="flex gap-1">
                                <Button 
                                  onClick={() => addVerseToDisplay(result)} 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 px-2 text-xs hover:bg-indigo-100"
                                  title="Mostrar en pantalla"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Ver
                                </Button>
                                <Button 
                                  onClick={() => shareVerse(result)} 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 px-2 text-xs hover:bg-purple-100"
                                  title="Compartir con todos"
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Compartir
                                </Button>
                              </div>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{result.text}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 py-8">
                        <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">Busca un versículo</p>
                        <p className="text-xs mt-1 text-gray-300">Ej: "Juan 3:16" o "amor"</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Panel de Notas Públicas */}
              {activePanel === 'notes' && (
                <>
                  <div className="p-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          <MessageSquare className="h-5 w-5" />
                          Notas Públicas
                        </h3>
                        <p className="text-xs opacity-80 flex items-center gap-1 mt-1">
                          <Users className="h-3 w-3" />
                          Todos pueden ver
                        </p>
                      </div>
                      <button onClick={() => setActivePanel(null)} className="hover:bg-white/20 rounded-full p-1 transition-colors">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {publicNotes.length === 0 ? (
                      <div className="text-center text-gray-400 py-8">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No hay notas aún</p>
                        <p className="text-xs mt-1">Sé el primero en compartir</p>
                      </div>
                    ) : (
                      publicNotes.map((note, idx) => (
                        <div 
                          key={note.id || idx} 
                          className={`p-3 rounded-xl text-sm ${
                            note.note_type === 'verse' 
                              ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200' 
                              : 'bg-gray-50 border border-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-800">{note.author_name}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(note.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className={note.note_type === 'verse' ? 'text-indigo-800' : 'text-gray-700'}>
                            {note.content}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-3 border-t bg-gray-50">
                    <div className="flex gap-2">
                      <Input
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendNote()}
                        placeholder="Escribe una nota..."
                        className="text-sm"
                        disabled={notesLoading}
                      />
                      <Button onClick={sendNote} size="sm" disabled={notesLoading || !newNote.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Barra de herramientas inferior */}
        <div className="px-4 pb-4">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 flex items-center justify-between">
            
            {/* Botones de herramientas */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setActivePanel(activePanel === 'bible' ? null : 'bible')}
                variant={activePanel === 'bible' ? 'default' : 'secondary'}
                size="sm"
                className={`gap-2 rounded-xl ${activePanel === 'bible' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-white/90 hover:bg-white text-gray-700'}`}
              >
                <BookOpen className="h-4 w-4" />
                Biblia
              </Button>
              <Button
                onClick={() => setActivePanel(activePanel === 'notes' ? null : 'notes')}
                variant={activePanel === 'notes' ? 'default' : 'secondary'}
                size="sm"
                className={`gap-2 rounded-xl ${activePanel === 'notes' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-white/90 hover:bg-white text-gray-700'}`}
              >
                <MessageSquare className="h-4 w-4" />
                Notas
                {publicNotes.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-1.5 min-w-[20px] text-center">
                    {publicNotes.length}
                  </span>
                )}
              </Button>
            </div>

            {/* Info central */}
            <div className="text-white/70 text-sm hidden sm:block">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                En vivo
              </span>
            </div>

            {/* Controles de ventana */}
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setIsFullscreen(!isFullscreen)}
                variant="secondary"
                size="sm"
                className="bg-white/90 hover:bg-white text-gray-700 rounded-xl"
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                onClick={() => {
                  // Si vino del widget (autostart), volver al dashboard
                  // Si es prueba de admin, solo cerrar la vista
                  if (autoStart) {
                    navigate('/app/dashboard');
                  } else {
                    setShowMeeting(false);
                  }
                }}
                variant="secondary"
                size="sm"
                className="bg-red-500/90 hover:bg-red-500 text-white rounded-xl gap-1"
              >
                <Home className="h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ FORMULARIO INICIAL (solo para pruebas de admin) ============
  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-lg mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg mb-4">
            <Video className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isStudent ? 'Unirse a Clase' : 'Prueba Zoom Embebido'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isStudent ? 'La clase se mostrará en esta página' : 'Configuración de prueba - Admin'}
          </p>
        </div>

        {/* Toggle para preview como estudiante */}
        {isRealAdmin && (
          <div className="flex justify-center">
            <Button
              onClick={() => setPreviewAsStudent(!previewAsStudent)}
              variant={previewAsStudent ? "default" : "outline"}
              className="gap-2 rounded-xl"
            >
              <Eye className="h-4 w-4" />
              {previewAsStudent ? 'Viendo como Estudiante' : 'Ver como Estudiante'}
            </Button>
          </div>
        )}

        {previewAsStudent && (
          <Alert className="bg-amber-50 border-amber-200">
            <Eye className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Modo Preview:</strong> Estás viendo como vería un estudiante
            </AlertDescription>
          </Alert>
        )}

        {/* Nombre de la clase desde Zoom */}
        {!loadingMeetingInfo && (
          <div className="text-center p-4 bg-white rounded-xl shadow-md">
            <p className="text-sm text-gray-500">Nombre de la reunión en Zoom:</p>
            <p className="text-xl font-semibold text-indigo-600">{className}</p>
          </div>
        )}

        {/* Card del formulario */}
        <Card className="shadow-xl border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <CardTitle className="text-lg font-medium">
              Datos de la Reunión
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            
            {/* Solo admin ve ID y contraseña */}
            {!isStudent && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-700">ID de Reunión</Label>
                  <Input
                    value={meetingNumber}
                    onChange={(e) => setMeetingNumber(e.target.value.replace(/\s/g, ''))}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Contraseña</Label>
                  <Input
                    value={meetingPassword}
                    onChange={(e) => setMeetingPassword(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-700">Tu Nombre</Label>
              <Input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Nombre que verán otros"
                className="rounded-xl"
              />
            </div>

            <Button 
              onClick={() => setShowPermissionModal(true)} 
              size="lg" 
              className="w-full mt-6 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg"
            >
              <Play className="h-5 w-5 mr-2" />
              {isStudent ? 'Ingresar a la Clase' : 'Iniciar Prueba'}
            </Button>
          </CardContent>
        </Card>

        {/* Info para flujo real */}
        <Alert className="bg-blue-50 border-blue-200">
          <Video className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            <strong>Nota:</strong> En producción, los estudiantes entrarán desde el widget de clases 
            sin ver este formulario. La URL incluirá los parámetros automáticamente:
            <code className="block mt-2 text-xs bg-blue-100 p-2 rounded">
              /app/zoom-test?meeting=ID&pwd=PASS&name=NOMBRE&autostart=true
            </code>
          </AlertDescription>
        </Alert>

        {/* Features para estudiantes */}
        {isStudent && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">Durante la clase tendrás acceso a:</p>
            <div className="flex justify-center gap-6">
              <div className="flex items-center gap-2 text-indigo-600">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Biblia</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-600">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">Notas Públicas</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoomSDKTest;
